"use client";

import Link from "next/link";
import { useLang } from "@/components/LangProvider";

const SECTION_INNER = "mx-auto max-w-site px-6 md:px-14";

export function AgricultureView() {
  const { t } = useLang();
  const a = t.agriculturePage;

  return (
    <div className="bg-background text-foreground overflow-hidden">
      {/* Page hero — split layout: text left + image right */}
      <section className="relative bg-paper text-foreground overflow-hidden">
        <div
          className={`${SECTION_INNER} grid gap-10 md:gap-[72px] md:grid-cols-[1fr_0.82fr] items-center pt-28 md:pt-[148px] pb-16 md:pb-24`}
        >
          {/* Text */}
          <div>
            <p
              className="text-mute font-medium uppercase font-sans mb-3"
              style={{ fontSize: 11, letterSpacing: "0.3em" }}
            >
              {a.eyebrow}
            </p>
            <h1
              className="font-display text-deep whitespace-pre-line"
              style={{
                fontWeight: 500,
                fontSize: "clamp(34px, 5vw, 64px)",
                lineHeight: 1.25,
                letterSpacing: "0.04em",
                margin: "14px 0 18px",
                textWrap: "balance",
              }}
            >
              {a.title}
            </h1>
            <p
              className="font-sans text-[#4a4a4a]"
              style={{ fontSize: 16, letterSpacing: "0.06em" }}
            >
              {a.sub}
            </p>
          </div>

          {/* Image */}
          <div className="relative">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/v2/agriculture/agriculture_hero.jpg"
              alt={a.title}
              className="block w-full aspect-[4/3] object-cover bg-[#1a2e21]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-20 md:py-28 bg-background">
        <div className={SECTION_INNER}>
          <div className="grid gap-12 md:grid-cols-[5fr_7fr] md:gap-20 md:items-start">
            <h2
              className="font-display text-deep m-0"
              style={{
                fontWeight: 500,
                fontSize: "clamp(28px, 3.6vw, 44px)",
                lineHeight: 1.4,
                letterSpacing: "0.04em",
              }}
            >
              {a.whyTitle}
            </h2>
            <p
              className="font-sans text-[#4a4a4a] m-0 max-w-[56ch] whitespace-pre-line"
              style={{ fontSize: 17, lineHeight: 2.1 }}
            >
              {a.whyBody}
            </p>
          </div>
        </div>
      </section>

      {/* Issues — 3 cols with border-right between, no num badges */}
      <section className="pb-20 md:pb-28 bg-paper">
        <div className={`${SECTION_INNER} pt-16`}>
          <div className="grid gap-8 md:gap-12 md:grid-cols-3 border-t border-rule pt-16">
            {a.issues.map((isu, i) => (
              <article
                key={isu.title}
                className={`flex flex-col gap-3 pr-6 ${
                  i < a.issues.length - 1 ? "md:border-r md:border-rule" : ""
                }`}
              >
                <h3
                  className="font-display text-deep m-0"
                  style={{
                    fontWeight: 500,
                    fontSize: 21,
                    lineHeight: 1.5,
                    letterSpacing: "0.04em",
                  }}
                >
                  {isu.title}
                </h3>
                <p
                  className="font-sans text-[#4a4a4a] m-0"
                  style={{ fontSize: 14, lineHeight: 2 }}
                >
                  {isu.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Approach — alternating steps, no step labels */}
      <section className="py-24 md:py-32 bg-background">
        <div className={SECTION_INNER}>
          <h2
            className="font-display text-deep m-0"
            style={{
              fontWeight: 500,
              fontSize: "clamp(28px, 3.6vw, 44px)",
              lineHeight: 1.4,
              letterSpacing: "0.04em",
            }}
          >
            {a.approachTitle}
          </h2>
          <ol className="list-none p-0 mt-14 grid gap-20 md:gap-24">
            {a.steps.map((s, i) => (
              <li
                key={s.title}
                className={`grid gap-10 md:gap-14 md:grid-cols-2 items-center ${
                  i % 2 === 1 ? "md:[&>:first-child]:order-2" : ""
                }`}
              >
                <div
                  className="relative w-full aspect-[4/3] overflow-hidden bg-[#1a2e21]"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={`/v2/agriculture/agriculture_step_${String(i + 1).padStart(2, "0")}.jpg`}
                    alt={a.stepPlaceholder(i)}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h3
                    className="font-display text-deep m-0"
                    style={{
                      fontWeight: 500,
                      fontSize: 26,
                      lineHeight: 1.5,
                      letterSpacing: "0.04em",
                      marginBottom: 12,
                    }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="font-sans text-[#4a4a4a] m-0"
                    style={{ fontSize: 14.5, lineHeight: 2 }}
                  >
                    {s.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Future — paper bg with plum CTA */}
      <section className="py-24 md:py-32 bg-paper text-foreground">
        <div className={SECTION_INNER}>
          <div className="grid gap-10 md:gap-20 md:grid-cols-[5fr_7fr] md:items-start">
            <div>
              <p
                className="text-mute font-medium uppercase font-sans mb-4"
                style={{ fontSize: 11, letterSpacing: "0.3em" }}
              >
                {a.futureLabel}
              </p>
              <h2
                className="font-display text-deep m-0"
                style={{
                  fontWeight: 500,
                  fontSize: "clamp(28px, 3.6vw, 44px)",
                  lineHeight: 1.4,
                  letterSpacing: "0.04em",
                }}
              >
                {a.futureTitle}
              </h2>
            </div>
            <p
              className="font-sans text-[#4a4a4a] max-w-[56ch] whitespace-pre-line m-0"
              style={{ fontSize: 15, lineHeight: 2.1 }}
            >
              {a.futureBody}
            </p>
          </div>

          <div className="mt-14">
            <Link
              href="/contact"
              className="inline-flex items-center gap-3 bg-accent-forest text-white px-6 py-3.5 font-medium uppercase font-sans hover:bg-accent-forest2 hover:-translate-y-px transition-all duration-200 no-underline"
              style={{
                fontSize: 12,
                letterSpacing: "0.2em",
                boxShadow: "0 6px 18px -8px rgba(31, 58, 43, 0.7)",
              }}
            >
              <span>{a.futureCta}</span>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M3 10h14M11 4l6 6-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
